import { DeleteOutlined } from '@ant-design/icons'
import { useInfiniteQuery } from '@tanstack/react-query'
import {
  Button,
  Divider,
  Empty,
  Flex,
  Form,
  List,
  SelectProps,
  Spin
} from 'antd'
import { FormInstance } from 'antd/lib'
import { Dispatch, useCallback, useEffect, useMemo, useState } from 'react'

import {
  useIdentityServiceGetProfilesListEndpointKey,
  usePreScreeningServiceGetRiskGroupPersonsEndpoint
} from 'src/api/queries'
import { GetPersonListResponse, PersonsService } from 'src/api/requests'
import { Select } from 'src/components'
import { useDebounce, useTablePagination } from 'src/hooks'
import { useLocales } from 'src/locales'
import { resetPagination } from 'src/utils'

import { useStyles } from './styles'

const DEFAULT_USER_LIST_PER_PAGE = 5
const DEFAULT_DEPARTMENT_USER_LIST_PER_PAGE = 50
const DEFAULT_USER_PER_PAGE = 50

type Props = {
  id?: string
  selectedUsersList: { value?: string; label?: string }[]
  setSelectedUsersList: Dispatch<
    React.SetStateAction<{ value?: string; label?: string }[]>
  >
  form: FormInstance
  institutionId?: string
  hasRiskGroup?: boolean
} & SelectProps

export const UsersSearch = ({
  id,
  selectedUsersList,
  setSelectedUsersList,
  institutionId,
  form,
  hasRiskGroup,
  ...rest
}: Props) => {
  const { t } = useLocales()
  const { styles } = useStyles()
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search)
  const [pageSize] = useState(DEFAULT_USER_LIST_PER_PAGE)

  const [selectedPage, setSelectedPage] = useState(1)

  const pagination = useTablePagination()

  const selectedUsers = Form.useWatch('personIds', form) || []

  const { data, fetchNextPage, isFetching } = useInfiniteQuery({
    queryKey: [
      useIdentityServiceGetProfilesListEndpointKey,
      debouncedSearch,
      institutionId
    ],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response = await PersonsService.getInstitutionPersonListEndpoint(
        pageParam,
        DEFAULT_USER_PER_PAGE,
        undefined,
        undefined,
        undefined,
        debouncedSearch,
        undefined,
        undefined,
        undefined,
        undefined,
        hasRiskGroup
      )

      return {
        data: response.data,
        total_pages: response.totalPages ?? 0,
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data?: GetPersonListResponse[]
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1
  })

  const userOptions = useMemo(() => {
    return data?.pages.flatMap(
      (page) =>
        page.data?.map((user) => ({
          value: user.id,
          label: `${user.firstName} ${user.lastName}`
        })) || []
    )
  }, [data])

  const { data: departmentUsers, isLoading: departmentUsersLoading } =
    usePreScreeningServiceGetRiskGroupPersonsEndpoint(
      {
        riskGroupId: String(id),
        pageNumber: Number(pagination.current),
        pageSize: DEFAULT_DEPARTMENT_USER_LIST_PER_PAGE
      },
      [String(id), String(institutionId)],
      { enabled: !!id && !!institutionId }
    )

  const onSelect = (value: string) => {
    const newUser = userOptions?.find((user) => user?.value === value)
    if (newUser) {
      setSelectedUsersList((prev) => [...prev, newUser])
      form.setFieldsValue({
        personIds: [...selectedUsers, value]
      })
    }

    setSearch('')
    resetPagination(pagination)
  }

  const onDeselect = (value?: string) => {
    setSelectedUsersList((prev) => prev?.filter((user) => user.value !== value))
    const updatedUsers = selectedUsers.filter(
      (userId: string) => userId !== value
    )
    form.setFieldsValue({ personIds: updatedUsers })
    setSearch('')
    setSelectedPage(1)
    resetPagination(pagination)
  }

  const updateSelectedUsersList = useCallback(() => {
    if (departmentUsers?.data) {
      const departmentUserIds = departmentUsers?.data?.map(
        (option) => option?.person?.id
      )

      const currentUserProfiles = departmentUserIds
        ?.map((id) => userOptions?.find((user) => user?.value === id))
        ?.filter((user): user is { value: string; label: string } => !!user)

      setSelectedUsersList(currentUserProfiles || [])

      form.setFieldsValue({
        personIds: Array.from(
          new Set([...(departmentUserIds || []), ...selectedUsers])
        )
      })
    }
  }, [id, form, departmentUsers?.data])

  useEffect(() => {
    updateSelectedUsersList()
  }, [id, departmentUsers?.data, form, updateSelectedUsersList])

  const paginatedSelectedUsers = selectedUsersList?.slice(
    (selectedPage - 1) * pageSize,
    selectedPage * pageSize
  )

  return (
    <>
      <Select
        filterOption={false}
        options={userOptions}
        onSearch={(value) => setSearch(value)}
        mode="multiple"
        maxTagCount={2}
        onClear={() => setSearch('')}
        loading={isFetching}
        placeholder={t('common.user')}
        showSearch={true}
        value={selectedUsers}
        onSelect={onSelect}
        onDeselect={onDeselect}
        onPopupScroll={(e) => {
          const target = e.target as HTMLElement
          if (target.scrollTop + target.offsetHeight === target.scrollHeight) {
            fetchNextPage()
          }
        }}
        notFoundContent={
          isFetching ? (
            <Flex justify="center">
              <Spin spinning />
            </Flex>
          ) : (
            <Empty />
          )
        }
        {...rest}
      />

      {!!selectedUsersList?.length && (
        <>
          <Divider />
          <List
            className={styles.listItem}
            itemLayout="horizontal"
            dataSource={paginatedSelectedUsers}
            loading={departmentUsersLoading}
            pagination={{
              current: selectedPage,
              pageSize,
              total: selectedUsersList.length,
              showTotal: (total) => `${t('common.total')} ${total}`,
              onChange: (page) => setSelectedPage(page),
              hideOnSinglePage: true
            }}
            renderItem={(user) => (
              <List.Item
                key={user.value}
                actions={[
                  <Button
                    key={user.value}
                    icon={<DeleteOutlined />}
                    onClick={() => onDeselect(user?.value)}
                    danger
                  />
                ]}
              >
                <List.Item.Meta title={user.label} />
              </List.Item>
            )}
          />
        </>
      )}
    </>
  )
}
